import clsx from "clsx"
import { makeStyles } from "@material-ui/core"
import dayjs from "dayjs"
import { FC, useMemo } from "react"

type Orientation = "horizontal" | "vertical"

export interface DBTTimeIntervalIconProps {
  /** ISO-like local datetime (YYYY-MM-DDTHH:mm) for the left/top pill */
  start: string
  /** ISO-like local datetime (YYYY-MM-DDTHH:mm) for the right/bottom pill */
  end: string
  /** Controls layout direction */
  orientation?: Orientation
  className?: string
}

interface TimeParts {
  time: string
  meridiem: string
}

const FALLBACK_TIME: TimeParts = { time: "--:--", meridiem: "" }

const formatTimeParts = (value: string): TimeParts => {
  if (!value) {
    return FALLBACK_TIME
  }

  const parsed = dayjs(value)

  if (!parsed.isValid()) {
    return FALLBACK_TIME
  }

  return {
    time: parsed.format("h:mm"),
    meridiem: parsed.format("A").toLowerCase(),
  }
}

const DBTTimeIntervalIcon: FC<DBTTimeIntervalIconProps> = ({
  start,
  end,
  orientation = "horizontal",
  className,
}) => {
  const classes = useStyles()

  const startParts = useMemo(() => formatTimeParts(start), [start])
  const endParts = useMemo(() => formatTimeParts(end), [end])

  const connector =
    orientation === "horizontal" ? (
      <svg
        className={clsx(classes.connector, classes.connectorHorizontal)}
        aria-hidden
        focusable="false"
        viewBox="0 0 8 23"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path d="M0 0 Q4 6 8 0 L8 23 Q4 17 0 23Z" fill="#F5F5F5" />
      </svg>
    ) : (
      <svg
        className={clsx(classes.connector, classes.connectorVertical)}
        aria-hidden
        focusable="false"
        viewBox="0 0 23 8"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path d="M0 0h23c-4 2-4 6 0 8H0c4-2 4-6 0-8Z" fill="#F5F5F5" />
      </svg>
    )

  return (
    <div
      className={clsx(
        classes.root,
        orientation === "horizontal" ? classes.horizontal : classes.vertical,
        className
      )}
    >
      <div className={classes.pill} data-node-id="start">
        <span className={classes.time}>{startParts.time}</span>
        {startParts.meridiem && (
          <span className={classes.meridiem}>{startParts.meridiem}</span>
        )}
      </div>

      {connector}

      <div className={classes.pill} data-node-id="end">
        <span className={classes.time}>{endParts.time}</span>
        {endParts.meridiem && (
          <span className={classes.meridiem}>{endParts.meridiem}</span>
        )}
      </div>
    </div>
  )
}

const useStyles = makeStyles({
  root: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  horizontal: {
    flexDirection: "row",
  },
  vertical: {
    flexDirection: "column",
  },
  pill: {
    height: 34,
    minWidth: 74,
    padding: "5px 6px",
    borderRadius: 8,
    borderWidth: 2,
    borderStyle: "solid",
    borderColor: "#F5F5F5",
    backgroundColor: "#F5F5F5",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    columnGap: 4,
    boxSizing: "border-box",
    textAlign: "center",
  },
  time: {
    fontFamily: "Lato",
    fontWeight: 700,
    fontSize: 16,
    lineHeight: "20px",
    color: "#474747",
  },
  meridiem: {
    fontFamily: "Lato",
    fontWeight: 400,
    fontSize: 12,
    lineHeight: "16px",
    color: "#474747",
    textTransform: "lowercase",
  },
  connector: {
    flexShrink: 0,
  },
  connectorHorizontal: {
    width: 8,
    height: 23,
    marginLeft: -1,
    marginRight: -1,
    display: "block",
  },
  connectorVertical: {
    width: 23,
    height: 8,
    marginTop: -1,
    marginBottom: -1,
    display: "block",
  },
})

export default DBTTimeIntervalIcon
